/**
 * WorkerComposerBanner — M4.4
 * Soft dismissible banner shown above the composer when the active thread
 * is a worker thread. Lets the user navigate back to the master thread.
 */
import { useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import type { EnvironmentId, ThreadId, ThreadKind } from "@cmmd-forge/contracts";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { deriveWorkerBannerVisibility } from "./WorkerComposerBanner.logic";

export interface WorkerComposerBannerProps {
  readonly kind: ThreadKind;
  readonly parentThreadId: ThreadId | null;
  readonly environmentId: EnvironmentId;
}

export function WorkerComposerBanner({
  kind,
  parentThreadId,
  environmentId,
}: WorkerComposerBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const visible = deriveWorkerBannerVisibility({ kind, parentThreadId, dismissed });
  if (!visible || parentThreadId === null) return null;

  const handleNavigateToMaster = () => {
    void navigate({
      to: "/$environmentId/$threadId",
      params: { environmentId, threadId: parentThreadId },
    });
  };

  return (
    <div
      className={cn(
        "mx-3 mb-2 sm:mx-5 flex items-center gap-2 rounded-md border",
        "border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning-foreground",
        "dark:border-warning-border dark:bg-warning/40 dark:text-warning-foreground",
      )}
      role="status"
    >
      <span className="flex-1 leading-snug">
        You are viewing a worker thread.{" "}
        <button
          type="button"
          className="underline underline-offset-2 hover:opacity-70 transition-opacity"
          onClick={handleNavigateToMaster}
        >
          View master thread
        </button>
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 shrink-0 text-warning-foreground hover:bg-warning-surface dark:hover:bg-warning/40"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
